import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Users, QrCode, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";

const colorFor = (status) =>
  status === "occupied" ? "#D95D39" : "#6A7F66";

export default function Tables() {
  const { hasRole } = useAuth();
  const { outletId } = useOutlet();
  const canManage = hasRole("manager");
  const nav = useNavigate();
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", capacity: 4, area: "Main" });
  const [qrTable, setQrTable] = useState(null);

  const load = () => api.get("/tables").then((r) => setTables(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, capacity: t.capacity, area: t.area });
    setOpen(true);
  };
  const startNew = () => {
    setEditing(null);
    setForm({ name: "", capacity: 4, area: "Main" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/tables/${editing.id}`, form);
      else await api.post("/tables", form);
      toast.success("Saved");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete ${t.name}?`)) return;
    try {
      await api.delete(`/tables/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const goBill = (t) => {
    if (t.current_order_id) nav(`/pos?order=${t.current_order_id}&pay=1`);
  };

  const tableQrUrl = (t) => {
    const base = window.location.origin;
    const oid = outletId || t.outlet_id || "";
    return `${base}/order/${oid}/${t.id}`;
  };

  const areas = [...new Set(tables.map((t) => t.area))];

  return (
    <div className="p-6 sm:p-8" data-testid="tables-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Tables</h1>
          <p className="text-sm text-[#7A7873] mt-1">Live floor view · tap an occupied table to resume billing.</p>
        </div>
        {canManage && (
          <Button onClick={startNew} className="bg-[#D95D39] hover:bg-[#C24F2C]" data-testid="add-table-btn">
            <Plus size={16} className="mr-1.5" /> Add Table
          </Button>
        )}
      </div>

      {areas.map((area) => (
        <div key={area} className="mb-8">
          <h2 className="font-heading font-bold text-lg text-[#2D2C2A] mb-3">{area}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {tables.filter((t) => t.area === area).map((t) => (
              <div
                key={t.id}
                className="bg-white border-2 rounded-lg p-4 transition hover:shadow-sm relative group"
                style={{ borderColor: colorFor(t.status) }}
                data-testid={`table-${t.name}`}
              >
                <div
                  className="absolute top-2 right-2 w-2 h-2 rounded-full"
                  style={{ backgroundColor: colorFor(t.status) }}
                />
                <div className="font-heading font-extrabold text-2xl text-[#2D2C2A]" data-testid={`table-name-${t.name}`}>
                  {t.name}
                </div>
                <div className="flex items-center gap-1 text-xs text-[#7A7873] mt-1">
                  <Users size={12} /> {t.capacity} seats
                </div>
                <div className="text-xs font-medium mt-2 capitalize" style={{ color: colorFor(t.status) }}>
                  {t.status}
                </div>
                {t.status === "occupied" && (
                  <div className="text-xs font-mono-num font-semibold text-[#2D2C2A] mt-0.5">
                    {inr(t.current_total)}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-1">
                  <Button
                    size="sm"
                    className="h-7 text-xs flex-1 bg-[#D95D39] hover:bg-[#C24F2C]"
                    onClick={() =>
                      nav(
                        t.current_order_id
                          ? `/pos?order=${t.current_order_id}`
                          : `/pos?table=${t.id}`
                      )
                    }
                    data-testid={`table-action-${t.name}`}
                  >
                    {t.status === "occupied" ? "Resume" : "Start"}
                  </Button>
                  {t.status === "occupied" && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-[#D95D39]"
                      title="Bill & Pay"
                      onClick={() => goBill(t)}
                      data-testid={`table-bill-${t.name}`}
                    >
                      <CreditCard size={12} />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    title="Show QR for self-order"
                    onClick={() => setQrTable(t)}
                    data-testid={`table-qr-${t.name}`}
                  >
                    <QrCode size={12} />
                  </Button>
                  {canManage && (
                    <>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(t)}>
                        <Pencil size={12} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => del(t)}>
                        <Trash2 size={12} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Table" : "Add Table"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="table-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Capacity</Label>
                <Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Area</Label>
                <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={save} data-testid="save-table">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Self-Order QR */}
      <Dialog open={!!qrTable} onOpenChange={(v) => !v && setQrTable(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Scan to Order · {qrTable?.name}</DialogTitle>
          </DialogHeader>
          {qrTable && (
            <div className="text-center print-area">
              <div className="font-heading font-bold text-lg text-[#2D2C2A] mb-1">Scan & Order</div>
              <div className="text-xs text-[#7A7873] mb-3">Table {qrTable.name}</div>
              <div className="inline-block p-3 bg-white border border-[#EBE8E2] rounded-md">
                <QRCodeSVG value={tableQrUrl(qrTable)} size={240} level="M" includeMargin />
              </div>
              <div className="text-[10px] text-[#7A7873] mt-2 break-all font-mono-num">{tableQrUrl(qrTable)}</div>
              <div className="text-xs text-[#7A7873] mt-2">Use phone camera · place your order · waiter will confirm.</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setQrTable(null)}>Close</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={() => window.print()}>Print QR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
